import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar, Filter } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchTimelineData,
  fetchInvestibleStructuresForFilter,
  setFilters,
  setSelectedYear,
  clearError,
} from '@/store/slices/timelineSlice';
import {
  GlassCard,
  GlassPageHeader,
  GlassButton,
  GlassSelect,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
} from '@/design-system';
import TimelineGrid from './components/TimelineGrid';

const TimelinePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    epics,
    investibleStructures,
    isLoading,
    isLoadingStructures,
    error,
    filters,
    selectedYear,
  } = useSelector((state: RootState) => state.timeline);

  const [showFilters, setShowFilters] = useState(true);

  // Filter EPICs to only show those with milestones in the selected year
  const filteredEpics = epics.filter((epic) => {
    if (!epic.milestones || epic.milestones.length === 0) {
      return false;
    }
    // Check if any milestone is in the selected year
    return epic.milestones.some((milestone) => {
      const milestoneYear = new Date(milestone.targetDate).getFullYear();
      return milestoneYear === selectedYear;
    });
  });

  // Fetch data on mount
  useEffect(() => {
    console.log('TimelinePage - Fetching data with filters:', filters);
    dispatch(fetchTimelineData(filters));
    dispatch(fetchInvestibleStructuresForFilter());
  }, [dispatch, filters]);

  // Debug logging
  useEffect(() => {
    console.log('TimelinePage - EPICs received:', epics);
    console.log('TimelinePage - Selected year:', selectedYear);
    console.log('TimelinePage - Epic count:', epics.length);
    if (epics.length > 0) {
      epics.forEach(epic => {
        console.log(`Epic: ${epic.name}, Milestones: ${epic.milestones?.length || 0}, Objectives: ${epic.objectives?.length || 0}`);
      });
    }
  }, [epics, selectedYear]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleFilterChange = (field: string, value: string) => {
    if (value === 'all') {
      const newFilters = { ...filters };
      delete newFilters[field as keyof typeof filters];
      dispatch(setFilters(newFilters));
    } else {
      dispatch(setFilters({ [field]: value }));
    }
  };

  const handleYearChange = (year: number) => {
    dispatch(setSelectedYear(year));
  };

  // Generate year range: current year ± 3 years (7 years total)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i);

  // Prepare options for GlassSelect
  const isOptions = [
    { value: 'all', label: 'All Investible Structures' },
    ...(investibleStructures || []).map((is) => ({
      value: is.id,
      label: `${is.name} (${is.category})`,
    })),
  ];

  const yearOptions = years.map((year) => ({
    value: year.toString(),
    label: `FY${year}`,
  }));

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Timeline View"
          subtitle="Visual timeline of EPICs, OKRs, and Milestones"
          icon={<Calendar className="w-6 h-6" />}
          iconGradient="blue-violet"
          className="mb-6"
          actions={
            <GlassButton
              variant="secondary"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="w-4 h-4" />}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </GlassButton>
          }
        />
      </SectionReveal>

      {/* Filters */}
      {showFilters && (
        <SectionReveal delay={0.1}>
          <GlassCard className="mb-6" padding="md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GlassSelect
                label="Investible Structure"
                value={filters.investibleStructureId || 'all'}
                onChange={(value) => handleFilterChange('investibleStructureId', value)}
                options={isOptions}
                disabled={isLoadingStructures}
              />
              <GlassSelect
                label="Fiscal Year"
                value={selectedYear.toString()}
                onChange={(value) => handleYearChange(parseInt(value))}
                options={yearOptions}
              />
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      {/* Error Display */}
      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Timeline Grid */}
      {isLoading ? (
        <SectionReveal delay={0.2}>
          <GlassCard padding="lg">
            <div className="flex items-center justify-center py-12">
              <GlassLoadingSpinner size="lg" variant="gradient" label="Loading timeline..." />
            </div>
          </GlassCard>
        </SectionReveal>
      ) : filteredEpics.length === 0 ? (
        <SectionReveal delay={0.2}>
          <GlassCard padding="lg">
            <div className="p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/60" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                No EPICs Found
              </h3>
              <p className="text-muted-foreground mb-4">
                {filters.investibleStructureId
                  ? 'No EPICs found for the selected Investible Structure.'
                  : 'No EPICs found. Create an EPIC with Milestones to see it on the timeline.'}
              </p>
              <div className="text-sm text-muted-foreground/60 space-y-2">
                <p><strong>Tip:</strong> EPICs will appear on the timeline when they have:</p>
                <ul className="list-disc list-inside text-left max-w-md mx-auto space-y-1 mt-2">
                  <li>At least one Milestone with a target date</li>
                  <li>Milestones in the selected fiscal year (FY{selectedYear})</li>
                </ul>
                <p className="mt-4">Try changing the fiscal year above to see milestones in other years.</p>
              </div>
            </div>
          </GlassCard>
        </SectionReveal>
      ) : (
        <SectionReveal delay={0.2}>
          <TimelineGrid epics={filteredEpics} selectedYear={selectedYear} />
        </SectionReveal>
      )}
    </div>
  );
};

export default TimelinePage;
